import { type StateCreator } from 'zustand';
import { EnergyType } from '@energy-retailer/constants';

export type Step = {
  fuel: EnergyType;
};

export type StepsSliceActions = {
  resetSteps: () => void;
  goToStep: (stepNumber: number) => void;
  setSteps: (steps: Step[]) => void;
  getCurrentStep: () => Step | null;
  setCurrentStep: (fuel: EnergyType) => void;
  getSteps: () => Step[];
  nextStep: () => void;
  previousStep: () => void;
  addStep: (fuel: EnergyType) => void;
  removeStep: (fuel: EnergyType) => void
  addElectricityStep: () => void;
  addGasStep: () => void;
}

export type StepsSliceState = {
  steps: Step[];
  currentStepNumber: number;
};

const initialState = {
  steps: [
    { fuel: EnergyType.Electricity },
    { fuel: EnergyType.Gas }
  ],
  currentStepNumber: 0,
}

export const StepsSlice: StateCreator<
  StepsSliceActions & StepsSliceState,
  [['zustand/devtools', never]],
  [],
  StepsSliceActions & StepsSliceState
> = (set, get) => ({
  ...initialState,
  resetSteps: () => {
    set(initialState, undefined, 'StepsSlice/resetSteps');
  },
  setSteps: (steps: Step[]) => {
    set({ steps }, undefined, 'StepsSlice/setSteps');
  },
  getCurrentStep: () => {
    const { currentStepNumber, steps } = get();
    if (currentStepNumber !== 0 && currentStepNumber !== 1) {
      // error
      return null;
    }
    return steps[currentStepNumber];
  },
  setCurrentStep: (fuel: EnergyType) => {
    const { steps } = get();
    const requestedStepIndex = steps.findIndex(step => step.fuel === fuel);

    set({ currentStepNumber: requestedStepIndex }, undefined, 'StepsSlice/setCurrentStep');
  },
  getSteps: () => {
    const { steps } = get();
    return steps;
  },
  goToStep: (stepNumber: number) => {
    if (stepNumber !== 0 && stepNumber !== 1) {
      // error
      return;
    }

    set({ currentStepNumber: stepNumber }, undefined, 'StepsSlice/goToStep');
  },
  nextStep: () => {
    const { currentStepNumber } = get();
    if (currentStepNumber === 0) {
      set({ currentStepNumber: 1 }, undefined, 'StepsSlice/nextStep')
    } else {
      // move on
    }
  },
  previousStep: () => {
    const { currentStepNumber } = get();
    if (currentStepNumber === 1) {
      set({ currentStepNumber: 0 }, undefined, 'StepsSlice/previousStep');
    } else {
      // move on
    }
  },
  addStep: (fuel: EnergyType) => {
    const { steps } = get();
    if (!steps.some(step => step.fuel === fuel)) {
      set({ steps: [...steps, createStep(fuel)] }, undefined, 'StepsSlice/addStep');
    }
  },
  addElectricityStep: () => {
    const { addStep, steps } = get();
    if (steps.find((step: Step) => step.fuel === EnergyType.Electricity)) {
      // error
      return;
    }
    addStep(EnergyType.Electricity);
  },
  addGasStep: () => {
    const { addStep, steps } = get();
    if (steps.find((step: Step) => step.fuel === EnergyType.Gas)) {
      // error
      return;
    }
    addStep(EnergyType.Gas);
  },
  removeStep: (fuel: EnergyType) => {
    const fuelStepNumber = get().steps.findIndex((step: Step) => step.fuel === fuel);
    set({ steps: get().steps.filter((step: Step) => step.fuel !== fuel) }, undefined,
      'StepsSlice/removeStep');
    if (get().currentStepNumber === fuelStepNumber) {
      set({ currentStepNumber: 0 }, undefined,
        'StepsSlice/removeStep');
    }
  }
});

function createStep(fuel: EnergyType): Step {
  return {
    fuel,
  };
}
